import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BalansEnResultatenrekening,
  BokkzVpbDataService,
  CorporateTaxEntity,
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, merge, Observable, shareReplay, take} from "rxjs";
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-belastingplichtige',
  templateUrl: './belastingplichtige.component.html',
  styleUrls: ['./belastingplichtige.component.scss'],
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiAlertModule,
    CommonModule
  ]
})
export class BelastingplichtigeComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzVpbDataService);
  private destroyRef = inject(DestroyRef);

  readonly corporateTaxEntity$: Observable<CorporateTaxEntity> =
    this.dataService.corporateTaxEntity$.pipe(
      filter((data): data is CorporateTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public boekjaarFg = new FormGroup({
    begindatum: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: [
        Validators.required,
      ],
    }),
    einddatum: new FormControl<Date | null>(null, {
      nonNullable: true,
      validators: [
        Validators.required,
      ],
    }),
  });

  public identificatieFg = new FormGroup({
    naamLichaam: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(200),
      ],
    }),
    fiscaalNummer: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/^\d{9}$/),
      ],
    }),
    voorlettersOndertekenaar: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(10),
      ],
    }),
    tussenvoegselOndertekenaar: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(10),
      ],
    }),
    achternaamOndertekenaar: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(200),
      ],
    }),
    functieOndertekenaar: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(40),
      ],
    }),
    telefoonnummerOndertekenaar: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/^\d{10}$/),
      ],
    }),
    beconnummer: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/^\d{6}$/),
      ],
    }),
    aangifteFiscaleEenheid: new FormControl<boolean>(false, {nonNullable: true}),
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: false, label: "Nee"},
    {value: true, label: "Ja"}
  ];

  createFormGroup(item?: BalansEnResultatenrekening): FormGroup {
    return new FormGroup({
      naamVennootschap: new FormControl<string | null>(item?.naamVennootschap ?? this.identificatieFg.getRawValue().naamLichaam ?? null),
      boekjaarVennootschap: new FormGroup({
        begindatum: new FormControl<Date | null>(item?.boekjaarVennootschap?.begindatum ?? this.boekjaarFg.getRawValue().begindatum ?? null),
        einddatum: new FormControl<Date | null>(item?.boekjaarVennootschap?.einddatum ?? this.boekjaarFg.getRawValue().einddatum ?? null),
      }),
      fiscaalNummer: new FormControl<string | null>(item?.fiscaalNummer ?? this.identificatieFg.getRawValue().fiscaalNummer ?? null),
      omschrijvingActiviteiten: new FormControl<string>(item?.omschrijvingActiviteiten ?? 'Activiteiten'),
      dochtermaatschappijFiscaleEenheid: new FormControl<boolean>(item?.dochtermaatschappijFiscaleEenheid ?? false),
      agrarischeActiviteiten: new FormControl<boolean>(item?.agrarischeActiviteiten ?? false),
    });
  }

  ngOnInit(): void {
    this.corporateTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      this.boekjaarFg.patchValue(data.aangifte?.boekjaar ?? {}, {emitEvent: false});
      this.identificatieFg.patchValue(data.aangifte?.identificatie ?? {}, {emitEvent: false});
    });

    merge(
      this.boekjaarFg.valueChanges,
      this.identificatieFg.valueChanges,
    ).pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.saveForm();
    });
  }

  ngOnDestroy(): void {
    this.saveForm();
  }

  saveForm(): void {
    if (this.boekjaarFg.invalid) {
      this.boekjaarFg.markAllAsTouched();
    }

    if (this.identificatieFg.invalid) {
      this.identificatieFg.markAllAsTouched();
    }

    this.identificatieFg.updateValueAndValidity({ emitEvent: false })
    this.boekjaarFg.updateValueAndValidity({ emitEvent: false })

    const identificatie = this.identificatieFg.getRawValue();
    const boekjaar = this.boekjaarFg.getRawValue();

    this.dataService.patchCorporateTaxEntity(currentEntity => ({
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          biljetSoort: 'A',

          boekjaar: {
            ...currentEntity.aangifte?.boekjaar,
            begindatum: boekjaar.begindatum ?? undefined,
            einddatum: boekjaar.einddatum ?? undefined,
          },

          identificatie: {
            ...currentEntity.aangifte?.identificatie,
            naamLichaam: identificatie.naamLichaam ?? undefined,
            fiscaalNummer: identificatie.fiscaalNummer ?? undefined,
            achternaamOndertekenaar: identificatie.achternaamOndertekenaar,
            // beconnummer: this.algDataService.intermediary.getValue().beconId,
            telefoonnummerOndertekenaar: identificatie.telefoonnummerOndertekenaar,
            aangifteFiscaleEenheid: identificatie.aangifteFiscaleEenheid,
          },

          balansEnResultatenrekening: {
            ...currentEntity.aangifte?.balansEnResultatenrekening,
            naamVennootschap: identificatie.naamLichaam ?? undefined,
            fiscaalNummer: identificatie.fiscaalNummer ?? undefined,
            boekjaarVennootschap: {
              ...currentEntity.aangifte?.balansEnResultatenrekening?.boekjaarVennootschap,
              begindatum: boekjaar.begindatum ?? undefined,
              einddatum: boekjaar.einddatum ?? undefined,
            },
            omschrijvingActiviteiten: 'Activiteiten',
            dochtermaatschappijFiscaleEenheid: false,
            agrarischeActiviteiten: currentEntity.aangifte?.balansEnResultatenrekening?.agrarischeActiviteiten ?? false,
          },

          vermogensvergelijking: {
            ...currentEntity.aangifte?.vermogensvergelijking,
          },
        }
      })
    );
  }
}
